import { useQuery } from "@apollo/client";
import { Alert, Button, LinearProgress, Typography } from "@mui/material";
import { ReactElement, useEffect, useMemo, useState } from "react";

import {
  CampaignEventType,
  GeneratePersonalizedGifsMutation,
  GetCampaignGeneratePersonalizedGifsJobQuery,
  PersonalizedGifStatus,
  SubaccountBudgetStateStatus,
  UserTenantRole,
  PartialSendEvaluationState,
} from "~frontend/__generated__/graphql";
import { DropdownButton } from "~frontend/components/DropdownButton";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import {
  GENERATE_PERSONALIZED_GIFS,
  GET_CAMPAIGN_FOR_VIEW,
  GET_CAMPAIGN_GENERATE_PERSONALIZED_GIFS_JOB,
} from "~frontend/graphql/campaigns";
import {
  useCurrentUser,
  useFeatureFlag,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { FullSendDialog } from "~frontend/routes/subaccounts/campaignView/initialSend/FullSendDialog";
import { PartialSendDialog } from "~frontend/routes/subaccounts/campaignView/initialSend/PartialSendDialog";
import { PickAWinnerDialog } from "~frontend/routes/subaccounts/campaignView/initialSend/PickAWinnerDialog";
import { palette } from "~frontend/theme";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import { useTextingHours } from "~frontend/util/textingHours";
import useMutation from "~frontend/util/useMutation";

import { CampaignForView } from "../types";
import { InitialSendFormDialog } from "./InitialSendFormDialog";
import { SendPreviewFormDialog } from "./SendPreviewFormDialog";
import { SendMode } from "./types";

const CAMPAIGN_EVENTS_FOR_ROI_COMPLETED = [
  CampaignEventType.InitialSendRoiTestCompletedWithoutWinner,
  CampaignEventType.InitialSendRoiTestWinnerChosen,
  CampaignEventType.InitialSendRoiTestSingleVariantFailed,
  CampaignEventType.InitialSendRoiTestSingleVariantPassed,
];

export function SendButtons({
  campaign,
  hasActBlueIntegration,
  revealSendStatus,
}: {
  campaign: CampaignForView;
  hasActBlueIntegration: boolean;
  revealSendStatus: () => void;
}): ReactElement {
  const currentUser = useCurrentUser()!;
  const subaccount = useSubaccountAdminConfig()!;

  const textingHours = {
    start: campaign.textingHoursStart ?? subaccount.textingHoursStart,
    end: campaign.textingHoursEnd ?? subaccount.textingHoursEnd,
    timezone: campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone,
  };

  const [displayedSendMode, setDisplayedSendMode] = useState<SendMode | null>(
    null
  );

  const [sendPreviewFormOpen, setSendPreviewFormOpen] = useState(false);

  const handleShowSendForm = (sendMode: SendMode) => {
    setDisplayedSendMode(sendMode);
  };

  const { isInTextingHours } = useTextingHours(textingHours);

  const canDoRoiTest = useMemo(
    () =>
      hasActBlueIntegration &&
      !campaign.events.some(event =>
        CAMPAIGN_EVENTS_FOR_ROI_COMPLETED.includes(event.type)
      ),
    [hasActBlueIntegration, campaign]
  );

  const campaignHasPersonalizedGifs =
    campaign.personalizedGifStatus !== null &&
    campaign.personalizedGifStatus !== PersonalizedGifStatus.None;
  const personalizedGifGenerationNeeded =
    campaign.personalizedGifStatus === PersonalizedGifStatus.NotGenerated;

  const approvalsNotMet =
    campaign.clickerApprovals.required > campaign.clickerApprovals.granted;

  const userIsOrgOwnerOrHigher = currentUser.tenantRole !== UserTenantRole.None;

  const [pickAWinnerOpen, setPickAWinnerOpen] = useState(false);

  const partialSendEvalsEnabled = useFeatureFlag(
    "enablePartialSendEvaluations"
  );
  const sendingDisabled =
    subaccount.sendingDisabled ||
    personalizedGifGenerationNeeded ||
    campaign.texterSendEnabled ||
    approvalsNotMet;

  if (partialSendEvalsEnabled) {
    // Pulls the last partial send evaluation (if one exists).
    // If the last evaluation failed and there exist multiple variants
    // then we show the pick a winner button.
    const showPickAWinner =
      campaign.partialSendEvaluations.length > 0 &&
      campaign.partialSendEvaluations[0].state ===
        PartialSendEvaluationState.Failed &&
      campaign.scriptVariants.filter(sv => !sv.deletedAt).length > 1;
    return (
      <>
        <FlexBox direction="row" gap={1} sx={{ pt: 1 }} alignItems="stretch">
          <Button
            variant="contained"
            onClick={() => setSendPreviewFormOpen(true)}
            disabled={subaccount.sendingDisabled}
            fullWidth
          >
            Preview Text
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleShowSendForm("all")}
            disabled={sendingDisabled}
          >
            Full Send
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleShowSendForm("partialSend")}
            disabled={sendingDisabled}
          >
            Partial Send
          </Button>
          {showPickAWinner && (
            <Button
              variant="contained"
              fullWidth
              // The user can schedule their send in the next texting hours so don't block on texting hours.
              disabled={sendingDisabled}
              onClick={() => setPickAWinnerOpen(true)}
            >
              Pick a Winner
            </Button>
          )}
          {subaccount.texterSendEnabled && (
            <Button
              variant="contained"
              fullWidth
              disabled={sendingDisabled || !isInTextingHours}
              onClick={() => handleShowSendForm("texterSend")}
            >
              Texter Send
            </Button>
          )}
        </FlexBox>
        {displayedSendMode === "all" && (
          <FullSendDialog
            campaign={campaign}
            handleClose={() => setDisplayedSendMode(null)}
          />
        )}
        {displayedSendMode === "partialSend" && (
          <PartialSendDialog
            campaign={campaign}
            handleClose={() => setDisplayedSendMode(null)}
          />
        )}
        {displayedSendMode === "texterSend" && (
          <InitialSendFormDialog
            sendMode={displayedSendMode}
            handleClose={() => setDisplayedSendMode(null)}
            campaign={campaign}
          />
        )}
        {pickAWinnerOpen && (
          <PickAWinnerDialog
            campaign={campaign}
            handleClose={() => setPickAWinnerOpen(false)}
          />
        )}
        {sendPreviewFormOpen && (
          <SendPreviewFormDialog
            handleClose={() => setSendPreviewFormOpen(false)}
            campaign={campaign}
            revealSendStatus={revealSendStatus}
          />
        )}
      </>
    );
  }

  return (
    <>
      <FlexBox
        direction="column"
        gap={1}
        sx={{ width: "458px", pt: 1 }}
        alignItems="center"
      >
        <FlexBox gap={"10px"} sx={{ width: "458px", pt: 1 }}>
          <Button
            onClick={() => setSendPreviewFormOpen(true)}
            variant="contained"
            sx={{
              minWidth: "146px",
            }}
            disabled={subaccount.sendingDisabled}
          >
            Preview Text
          </Button>
          <DropdownButton
            label="Send"
            width="146px"
            disabled={sendingDisabled || !isInTextingHours}
            menuItems={[
              {
                key: "send-portion",
                label: "Send to a Portion of Your List",
                onClick: () => handleShowSendForm("portion"),
              },
              {
                key: "send-all",
                label: "Send to Your Full List",
                onClick: () => handleShowSendForm("all"),
              },
              ...(canDoRoiTest
                ? [
                    {
                      key: "send-roi",
                      label: "Autosend Based on ROI",
                      onClick: () => handleShowSendForm("roiTest"),
                    },
                  ]
                : []),
              ...(subaccount.texterSendEnabled
                ? [
                    {
                      key: "enable-texter-send",
                      label: "Texter Send",
                      onClick: () => handleShowSendForm("texterSend"),
                    },
                  ]
                : []),
            ]}
          />

          <DropdownButton
            label="Schedule"
            width="146px"
            disabled={sendingDisabled}
            menuItems={[
              {
                key: "schedule-portion",
                label: "Schedule a Portion of Your List",
                onClick: () => handleShowSendForm("schedulePortion"),
              },
              {
                key: "schedule-send",
                label: "Schedule Your Full List",
                onClick: () => handleShowSendForm("schedule"),
              },
              ...(canDoRoiTest
                ? [
                    {
                      key: "send-roi",
                      label: "Schedule Autosend Based on ROI",
                      onClick: () => handleShowSendForm("scheduleRoiTest"),
                    },
                  ]
                : []),
            ]}
          />
        </FlexBox>
        {subaccount.budgetStatus.status ===
          SubaccountBudgetStateStatus.Exceeded && (
          <Typography variant="body2" sx={{ color: palette.darkGray }}>
            Messaging is paused on this subaccount because the budget has been
            exceeded.{" "}
            <InternalLink
              to={
                userIsOrgOwnerOrHigher
                  ? "/organization/budgets"
                  : `mailto:${subaccount.budgetStatus.orgOwners!.map(oo => oo.email).join(",")}`
              }
              target="_blank"
            >
              Increase the budget to start sending again.
            </InternalLink>
          </Typography>
        )}
        {approvalsNotMet && (
          <Typography variant="body2" sx={{ color: palette.darkGray }}>
            You must finish approving contacts before you can send initial
            messages.
          </Typography>
        )}
        {campaignHasPersonalizedGifs && (
          <GeneratePersonalizedGifs campaign={campaign} />
        )}
      </FlexBox>
      {!!displayedSendMode && displayedSendMode !== "partialSend" && (
        <InitialSendFormDialog
          sendMode={displayedSendMode}
          handleClose={() => setDisplayedSendMode(null)}
          campaign={campaign}
        />
      )}
      {sendPreviewFormOpen && (
        <SendPreviewFormDialog
          handleClose={() => setSendPreviewFormOpen(false)}
          campaign={campaign}
          revealSendStatus={revealSendStatus}
        />
      )}
    </>
  );
}

function GeneratePersonalizedGifs({
  campaign,
}: {
  campaign: CampaignForView;
}): ReactElement {
  const [generatePersonalizedGifs, { loading, error }] =
    useMutation<GeneratePersonalizedGifsMutation>(GENERATE_PERSONALIZED_GIFS, {
      refetchQueries: [
        GET_CAMPAIGN_FOR_VIEW,
        GET_CAMPAIGN_GENERATE_PERSONALIZED_GIFS_JOB,
      ],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: "Error triggering personalized gif generation",
      variables: {
        input: {
          campaignId: campaign.id,
        },
      },
    });

  const lastJob = campaign.generatePersonalizedGifsJob;

  if (!lastJob) {
    return (
      <>
        {error && (
          <Alert severity="error">
            Failed to trigger GIF generation job: {error.message}
          </Alert>
        )}
        <Button
          onClick={() =>
            generatePersonalizedGifs({
              variables: { campaignId: campaign.id },
            })
          }
          sx={{ minWidth: "458px" }}
          disabled={loading}
          variant="contained"
        >
          {error ? "Retry" : "Generate Personalized Gifs"}
        </Button>
        <Typography variant="body2">
          Please generate your personalized GIFs in order to start sending.
        </Typography>
      </>
    );
  }

  return (
    <GifJobProgress
      shortId={campaign.shortId}
      retryGeneratingGifs={() => generatePersonalizedGifs()}
    />
  );
}

function GifJobProgress({
  shortId,
  retryGeneratingGifs,
}: {
  shortId: number;
  retryGeneratingGifs: () => void;
}): ReactElement {
  const { loading, error, data, refetch, startPolling, stopPolling } =
    useQuery<GetCampaignGeneratePersonalizedGifsJobQuery>(
      GET_CAMPAIGN_GENERATE_PERSONALIZED_GIFS_JOB,
      {
        variables: {
          input: {
            shortId: shortId,
          },
        },
      }
    );

  useEffect(() => {
    startPolling(1000);
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  if (error) {
    setTimeout(() => refetch(), 5000);
    return <Alert severity="error">Failed to fetch job info</Alert>;
  }
  if (!data) {
    return <Loading />;
  }

  // This component shouldn't get rendered unless the campaign has a job to
  // generate personalized gifs.
  const job = data.getCampaignByShortId.generatePersonalizedGifsJob!;

  if (jobInProgress(job)) {
    return (
      <FlexBox
        gap={1}
        alignItems="center"
        sx={{ width: "100%" }}
        direction="column"
      >
        <FlexBox gap="10px" alignItems="center">
          <LinearProgress
            sx={{ width: "425px" }}
            variant="determinate"
            value={job.progress * 100}
          />
          {(job.progress * 100).toFixed(0)}%
        </FlexBox>
        <Typography variant="body2">Generating GIFs...</Typography>
      </FlexBox>
    );
  }

  stopPolling();

  const { failed, errorMessage } = jobFailed(job);
  if (failed) {
    return (
      <>
        <Alert sx={{ minWidth: "458px" }} severity="error">
          GIF generation failed: {errorMessage}
        </Alert>
        <Button
          onClick={retryGeneratingGifs}
          sx={{ minWidth: "458px" }}
          disabled={loading}
          variant="contained"
        >
          Retry Generating GIFs
        </Button>
      </>
    );
  }

  // Otherwise the job must be complete.
  return <>Personalized GIF generation complete.</>;
}
